.section {
  background: var(--color-primary-grey-000); /* PRIMARY_GREY_000 — white band between the blue hero and the cream form */
  padding: 76px 0;
}

.section__head {
  /* Matches the Press page's section head so the title stays on one line on wide screens. */
  max-width: 860px;
  margin-bottom: 40px;
}

.section__title {
  font-family: var(--plume-font-display, sofia-pro, sans-serif);
  font-weight: 900;
  font-size: clamp(32px, 4.4vw, 52px);
  line-height: 1.02;
  letter-spacing: -0.02em;
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
  margin: 0 0 14px;
}

.section__lede {
  font-size: 19px;
  line-height: 1.5;
  color: var(--color-primary-sand-900); /* text-2 */
  margin: 0;
}

/* ---------- Accordion ---------- */
.list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.acc {
  --acc: var(--color-primary-grey-900); /* per-variant hover/open shadow colour (set below) */
  background: var(--color-primary-grey-000); /* PRIMARY_GREY_000 */
  border: var(--plume-border-card, 2px solid var(--color-primary-grey-900));
  border-radius: 16px;
  box-shadow: var(--plume-shadow-card, 6px 6px 0 var(--color-primary-grey-900));
  overflow: hidden;
  transition:
    transform 0.22s cubic-bezier(0.04, 0.4, 0.5, 0.95),
    box-shadow 0.22s cubic-bezier(0.04, 0.4, 0.5, 0.95);
}

.acc:hover {
  transform: translate(-2px, -2px);
}

/* hover + open both lift the sticker shadow to the profile's own colour */
.acc:hover,
.accIsOpen {
  box-shadow: 8px 8px 0 var(--acc);
}

.accBlue {
  --acc: var(--color-primary-blue-500); /* blue-500 */
}
.accBlue .acc__icon {
  background: var(--color-primary-blue-500); /* blue-500 */
  color: var(--color-primary-grey-000);
}

.accYellow {
  --acc: var(--color-complementary-yellow); /* COMPLEMENTARY_YELLOW */
}
.accYellow .acc__icon {
  background: var(--color-complementary-yellow); /* COMPLEMENTARY_YELLOW */
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
}

.accGreen {
  --acc: var(--color-secondary-green-800); /* SECONDARY_GREEN_800 */
}
.accGreen .acc__icon {
  background: var(--color-secondary-green-800); /* SECONDARY_GREEN_800 */
  color: var(--color-primary-grey-000);
}

.accRed {
  --acc: var(--color-primary-red-500); /* PRIMARY_RED_500 */
}
.accRed .acc__icon {
  background: var(--color-primary-red-500); /* PRIMARY_RED_500 */
  color: var(--color-primary-grey-000);
}

.acc__head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 22px 26px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.acc__icon {
  flex: none;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.acc__title {
  flex: 1;
  min-width: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
}

.acc__chev {
  flex: none;
  display: inline-flex;
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
  transform: rotate(90deg);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.accIsOpen .acc__chev {
  transform: rotate(-90deg);
}

/* Height animation via animatable grid-template-rows (0fr → 1fr). */
.acc__panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s cubic-bezier(0.04, 0.4, 0.5, 0.95);
}

.accIsOpen .acc__panel {
  grid-template-rows: 1fr;
}

.acc__body {
  overflow: hidden;
  padding: 0 26px;
}

.accIsOpen .acc__body {
  padding-bottom: 26px;
}

.acc__text {
  font-size: 16px;
  line-height: 1.5; /* COPY_M */
  color: var(--color-primary-sand-900); /* text-2 */
  margin: 0 0 18px;
}

.acc__cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  margin-bottom: 18px;
}

.acc__seclink {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 15px;
  font-weight: 700;
  color: var(--color-primary-blue-700); /* PRIMARY_BLUE_700 */
  transition: color 0.18s cubic-bezier(0.04, 0.4, 0.5, 0.95);
}

.acc__seclink:hover {
  color: var(--color-primary-blue-900); /* PRIMARY_BLUE_900 */
}

.acc__faq {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 18px;
  padding-top: 18px;
  border-top: 2px solid var(--color-primary-sand-200); /* PRIMARY_SAND_200 */
}

.acc__faqLabel {
  font-size: 11px; /* COPY_XS */
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--color-text-tertiary); /* text-3 */
}

.acc__faqLink {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px; /* COPY_S */
  font-weight: 700;
  color: var(--color-primary-blue-700); /* PRIMARY_BLUE_700 */
  transition: color 0.18s cubic-bezier(0.04, 0.4, 0.5, 0.95);
}

.acc__faqLink:hover {
  color: var(--color-primary-red-500); /* PRIMARY_RED_500 */
}

@media (max-width: 560px) {
  .acc__title {
    font-size: 16px;
  }
}
